'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Our Team', href: '/about#team' },
      { label: 'Certifications', href: '/about#certifications' },
      { label: 'Careers', href: '/about#careers' },
    ],
    solutions: [
      { label: 'Banking Uniforms', href: '/solutions#banking' },
      { label: 'Aviation Wear', href: '/solutions#aviation' },
      { label: 'Government Attire', href: '/solutions#government' },
      { label: 'Corporate Solutions', href: '/solutions#corporate' },
    ],
    resources: [
      { label: 'Case Studies', href: '/solutions#cases' },
      { label: 'Uniform Care Guide', href: '/solutions#care' },
      { label: 'Size Charts', href: '/solutions#sizing' },
      { label: 'FAQs', href: '/about#faq' },
    ],
    contact: [
      { label: 'Request Consultation', href: '/contact' },
      { label: 'Client Portal', href: '/contact#portal' },
      { label: 'Support', href: '/contact#support' },
      { label: 'Locations', href: '/contact#locations' },
    ],
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftIcon', href: '#' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', href: '#' },
    { name: 'Instagram', icon: 'CameraIcon', href: '#' },
  ];

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-brand-gold"
              >
                <path
                  d="M20 2L4 12L20 22L36 12L20 2Z"
                  fill="currentColor"
                  opacity="0.9"
                />
                <path
                  d="M20 22L4 12V28L20 38L36 28V12L20 22Z"
                  fill="var(--color-accent)"
                  opacity="0.7"
                />
                <circle cx="20" cy="20" r="3" fill="white" />
              </svg>
              <span className="text-xl font-headline font-semibold">Pink Diamond</span>
            </div>
            <p className="text-sm font-body text-gray-300 mb-4 leading-relaxed">
              Tanzania's premier provider of corporate uniform solutions, trusted by leading institutions for over 15 years.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-white bg-opacity-10 rounded-full flex items-center justify-center hover:bg-brand-gold transition-colors duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Solutions</h3>
            <ul className="space-y-2">
              {footerLinks.solutions.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Contact</h3>
            <ul className="space-y-2">
              {footerLinks.contact.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white border-opacity-20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm font-body text-gray-300">
              {isHydrated ? `© ${currentYear}` : '© 2026'} Pink Diamond Company Limited. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link
                href="/privacy"
                className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
              >
                Terms of Service
              </Link>
              <Link
                href="/cookies"
                className="text-sm font-body text-gray-300 hover:text-brand-gold transition-colors duration-300"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;